import i18n from '@/utils/i18n';
import React from 'react';
import { ArrowDirection } from '@sales-portal/domain/pricing/screens/management/types';
import { createStyles } from '@/contexts/theme-context';
import { formatCompactCurrency } from '@sales-portal/blocs/utils/money';
import { Icons, XStack } from '@usepepper/sprout-kit-ui';
import { Pressable } from 'react-native';
import { resolveDelta } from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { Typography } from '@usepepper/ui/atoms/typography';

interface Props {
  currencyCode: string;
  editCount: number;
  onPress: () => void;
  weeklyImpactInMicros: number;
}

export const PricingPendingEditsBanner: React.FC<Props> = ({
  currencyCode,
  editCount,
  onPress,
  weeklyImpactInMicros,
}) => {
  const [styles, theme] = useStyles();

  const label = i18n.t(
    editCount === 1
      ? 'pricing.pending_edits.singular'
      : 'pricing.pending_edits.plural',
    { count: editCount },
  );

  // A gain takes an explicit "+"; a loss already carries its minus sign. An
  // amount that rounds to zero is neutral, so it takes neither.
  const { direction, formattedValue } = resolveDelta(
    weeklyImpactInMicros,
    (value) => formatCompactCurrency(value, currencyCode),
  );
  const sign = direction === ArrowDirection.Up ? '+' : '';
  const impact = i18n.t('pricing.overview.impact_per_week', {
    value: `${sign}${formattedValue}`,
  });

  return (
    <Pressable
      accessibilityLabel={`${label} ${impact}`}
      accessibilityRole="link"
      onPress={onPress}
      style={styles.banner}
    >
      <XStack alignItems="center" justifyContent="space-between">
        <XStack alignItems="baseline" gap={theme.sk.spacing.spaceXsmall}>
          <Typography color={theme.sk.color.text.blue} variant="label-md">
            {label}
          </Typography>
          <Typography color={theme.sk.color.text.blue} variant="paragraph-md">
            {impact}
          </Typography>
        </XStack>
        <XStack alignItems="center" gap={theme.sk.spacing.spaceXsmall}>
          <Typography color={theme.sk.color.text.blue} variant="label-md">
            {i18n.t('pricing.pending_edits.review')}
          </Typography>
          <Icons.ChevronRight color={theme.sk.color.text.blue} size={16} />
        </XStack>
      </XStack>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  banner: {
    backgroundColor: theme.sk.color.bg.highlight.blue.default,
    borderRadius: theme.sk.radii.radiusMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
